import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DoctorReview from "@/components/DoctorReview";
import { ImageLightbox } from "@/components/ImageLightbox";
import {
  Plus, Trash2, Search, Camera, ChevronDown, ChevronUp,
  FileText, AlertCircle, CheckCircle, Clock, ArrowLeft, Upload,
  Info, Edit2, X, Send, ClipboardList, ArrowLeftRight, Activity,
  Download, Printer, Home
} from "lucide-react";
import BottomNav from "./BottomNav";
import { compressImage } from "@/utils/compressImage";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("session_token");
  return fetch(url, {
    ...options,
    headers: { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

export default function UlcersPage() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [caseLoading, setCaseLoading] = useState(false);

  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [patientForm, setPatientForm] = useState({ patient_name: "", patient_surname: "", patient_id: "" });

  const [uploadingFor, setUploadingFor] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);

  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ patient_name: "", patient_surname: "", patient_id: "" });
  const [saving, setSaving] = useState(false);

  const [lightboxImage, setLightboxImage] = useState(null);

  const [questionnaireSession, setQuestionnaireSession] = useState(null);
  const [comparing, setComparing] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/ulcers/cases`);
      if (res.ok) setCases(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const fetchCase = async (caseId) => {
    setCaseLoading(true);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${caseId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedCase(data.case);
        setSessions(data.sessions);
      }
    } catch (e) { console.error(e); }
    finally { setCaseLoading(false); }
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/ulcers/cases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patientForm),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Patsient lisatud");
        setNewPatientOpen(false);
        setPatientForm({ patient_name: "", patient_surname: "", patient_id: "" });
        fetchCases();
        fetchCase(data.case_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Lisamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
  };

  const handleUploadPhoto = async () => {
    if (!photo) { toast.error("Haavapilt on kohustuslik"); return; }
    setUploading(true);
    try {
      const compressed = await compressImage(photo);
      const formData = new FormData();
      formData.append("image", compressed);
      const res = await authFetch(`${API}/ulcers/cases/${uploadingFor}/sessions`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Pilt üles laetud!");
        setPhoto(null);
        setUploadingFor(null);
        fetchCase(selectedCase.case_id);
        handleAnalyze(selectedCase.case_id, data.session_id);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Üleslaadimine ebaõnnestus");
      }
    } catch (e) { toast.error("Üleslaadimine ebaõnnestus"); }
    finally { setUploading(false); }
  };

  const handleAnalyze = async (caseId, sessionId) => {
    setAnalyzing(sessionId);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${caseId}/sessions/${sessionId}/analyze`, { method: "POST" });
      if (res.ok) {
        toast.info("AI analüüs alustatud, palun oodake...");
        pollAnalysisStatus(caseId, sessionId);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Analüüsi alustamine ebaõnnestus");
        setAnalyzing(null);
      }
    } catch (e) {
      toast.error("Analüüsi alustamine ebaõnnestus");
      setAnalyzing(null);
    }
  };

  const pollAnalysisStatus = async (caseId, sessionId) => {
    const maxAttempts = 40;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await authFetch(`${API}/ulcers/cases/${caseId}/sessions/${sessionId}/status`);
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status === "analyzed" || data.status === "completed") {
          toast.success("TIMERS analüüs valmis!");
          setAnalyzing(null);
          fetchCase(caseId);
          return;
        }
        if (data.status === "failed") {
          toast.error("AI analüüs ebaõnnestus. Proovige uuesti.");
          setAnalyzing(null);
          fetchCase(caseId);
          return;
        }
      } catch (e) { /* continue polling */ }
    }
    toast.error("Analüüs võtab oodatust kauem. Värskendage lehte hiljem.");
    setAnalyzing(null);
    fetchCase(caseId);
  };

  const handleCompare = async (currentSessionId, previousSessionId) => {
    setComparing(currentSessionId);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${selectedCase.case_id}/sessions/${currentSessionId}/compare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ previous_session_id: previousSessionId }),
      });
      if (res.ok) {
        const data = await res.json();
        setComparisonResult({ sessionId: currentSessionId, result: data.result, data: data.comparison_data });
        toast.success("Võrdlus valmis!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Võrdlus ebaõnnestus");
      }
    } catch (e) { toast.error("Võrdlus ebaõnnestus"); }
    finally { setComparing(null); }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm("Kas olete kindel? Kõik selle patsiendi andmed kustutatakse.")) return;
    try {
      const res = await authFetch(`${API}/ulcers/cases/${caseId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Patsient kustutatud");
        setSelectedCase(null);
        setSessions([]);
        fetchCases();
      }
    } catch (e) { toast.error("Kustutamine ebaõnnestus"); }
  };

  const downloadPdf = async (url, fallbackName) => {
    toast.info("PDF genereeritakse...");
    try {
      const res = await authFetch(url);
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        toast.error(err?.detail || "PDF allalaadimine ebaõnnestus");
        return;
      }
      const blob = await res.blob();
      if (blob.size < 100) {
        toast.error("PDF genereerimine ebaõnnestus – tühi fail");
        return;
      }
      const disposition = res.headers.get("Content-Disposition");
      let filename = fallbackName;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("PDF allalaaditud!");
    } catch (e) {
      console.error("PDF download error:", e);
      toast.error("PDF allalaadimine ebaõnnestus. Proovige uuesti.");
    }
  };

  const printPdf = async (url) => {
    toast.info("Genereerin...");
    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const pdfUrl = URL.createObjectURL(blob);
      const w = window.open(pdfUrl);
      if (w) w.onload = () => w.print();
    } catch {
      toast.error("Printimine ebaõnnestus");
    }
  };

  const openEditCase = () => {
    setEditForm({
      patient_name: selectedCase.patient_name,
      patient_surname: selectedCase.patient_surname,
      patient_id: selectedCase.patient_id,
    });
    setEditDialogOpen(true);
  };

  const handleEditCase = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${selectedCase.case_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        toast.success("Andmed uuendatud");
        setEditDialogOpen(false);
        fetchCase(selectedCase.case_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Uuendamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
    finally { setSaving(false); }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Kas olete kindel?")) return;
    try {
      const res = await authFetch(`${API}/ulcers/cases/${selectedCase.case_id}/sessions/${sessionId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Visiit kustutatud");
        fetchCase(selectedCase.case_id);
      }
    } catch (e) { toast.error("Kustutamine ebaõnnestus"); }
  };

  const [notifying, setNotifying] = useState(false);
  const handleNotifyDoctor = async () => {
    setNotifying(true);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${selectedCase.case_id}/notify-doctor`, { method: "POST" });
      if (res.ok) toast.success("E-post saadetud arstile");
      else {
        const err = await res.json();
        toast.error(err.detail || "Saatmine ebaõnnestus");
      }
    } catch (e) { toast.error("Saatmine ebaõnnestus"); }
    finally { setNotifying(false); }
  };

  const getImageUrl = (path) => `${API}/ulcers/image/${path}`;

  const filteredCases = cases.filter(c => {
    const q = searchQuery.toLowerCase();
    return c.patient_name.toLowerCase().includes(q) ||
      c.patient_surname.toLowerCase().includes(q) ||
      c.patient_id.includes(q);
  });

  // ==================== CASE DETAIL VIEW ====================
  if (selectedCase) {
    return (
      <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="ulcer-case-detail">
        <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="md:ml-64 min-h-screen">
          <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
                <Button variant="ghost" size="sm" onClick={() => { setSelectedCase(null); setSessions([]); fetchCases(); }} data-testid="ulcer-back-to-list">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Tagasi
                </Button>
                <div>
                  <h1 className="text-lg md:text-xl font-bold text-slate-900">
                    {selectedCase.patient_name} {selectedCase.patient_surname}
                  </h1>
                  <p className="text-sm text-slate-500">IK: {selectedCase.patient_id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={openEditCase} data-testid="ulcer-edit-case-btn">
                  <Edit2 className="w-4 h-4 mr-1" /> Muuda
                </Button>
                <Button size="sm" onClick={() => setUploadingFor(selectedCase.case_id)} className="bg-slate-900 hover:bg-slate-800" data-testid="ulcer-new-session-btn">
                  <Camera className="w-4 h-4 mr-1" /> Uus visiit
                </Button>
                <Button size="sm" variant="outline" onClick={() => printPdf(`${API}/ulcers/cases/${selectedCase.case_id}/pdf`)} className="text-slate-600 border-slate-200" data-testid="ulcer-print-history-pdf">
                  <Printer className="w-4 h-4 mr-1" /> Prindi
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(`${API}/ulcers/cases/${selectedCase.case_id}/pdf`, "TIMERS_ajalugu.pdf")} className="text-emerald-600 border-emerald-200" data-testid="ulcer-download-history-pdf">
                  <Download className="w-4 h-4 mr-1" /> Ajalugu PDF
                </Button>
                <HomeCareGuideButton caseId={selectedCase.case_id} surname={selectedCase.patient_surname} />
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleDeleteCase(selectedCase.case_id)} data-testid="ulcer-delete-case-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
            <UlcerGuidelinesCard open={guidelinesOpen} onToggle={() => setGuidelinesOpen(!guidelinesOpen)} />

            {uploadingFor && (
              <UlcerUploadCard
                photo={photo}
                setPhoto={setPhoto}
                uploading={uploading}
                onUpload={handleUploadPhoto}
                onCancel={() => { setUploadingFor(null); setPhoto(null); }}
              />
            )}

            {caseLoading ? (
              <div className="flex items-center justify-center h-32"><div className="w-8 h-8 spinner" /></div>
            ) : sessions.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-8 text-center">
                  <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">Visiite pole veel lisatud</p>
                  <p className="text-sm text-slate-400 mt-1">Klikkige &quot;Uus visiit&quot; alustamiseks</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sessions.map((sess, idx) => (
                  <UlcerSessionCard
                    key={sess.session_id}
                    session={sess}
                    caseId={selectedCase.case_id}
                    index={sessions.length - idx}
                    analyzing={analyzing === sess.session_id}
                    onAnalyze={() => handleAnalyze(selectedCase.case_id, sess.session_id)}
                    onDelete={() => handleDeleteSession(sess.session_id)}
                    getImageUrl={getImageUrl}
                    onImageClick={setLightboxImage}
                    onQuestionnaire={() => setQuestionnaireSession(sess)}
                    previousSession={idx < sessions.length - 1 ? sessions[idx + 1] : null}
                    onCompare={(prevId) => handleCompare(sess.session_id, prevId)}
                    comparing={comparing === sess.session_id}
                    comparisonResult={comparisonResult?.sessionId === sess.session_id ? comparisonResult.result : null}
                    comparisonData={comparisonResult?.sessionId === sess.session_id ? comparisonResult.data : null}
                    onDownloadPdf={() => downloadPdf(`${API}/ulcers/cases/${selectedCase.case_id}/sessions/${sess.session_id}/pdf`, `TIMERS_visiit_${sess.session_id.slice(-6)}.pdf`)}
                    onPrintPdf={() => printPdf(`${API}/ulcers/cases/${selectedCase.case_id}/sessions/${sess.session_id}/pdf`)}
                    onRefreshSessions={() => fetchCase(selectedCase.case_id)}
                    isDoctor={!!user?.is_doctor}
                  />
                ))}
              </div>
            )}
          </main>
        </div>

        {lightboxImage && (
          <ImageLightbox src={lightboxImage.src} alt={lightboxImage.label} onClose={() => setLightboxImage(null)} />
        )}

        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Muuda patsiendi andmeid</DialogTitle></DialogHeader>
            <form onSubmit={handleEditCase} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Eesnimi *</Label>
                  <Input value={editForm.patient_name} onChange={e => setEditForm({ ...editForm, patient_name: e.target.value })} required data-testid="edit-ulcer-name" />
                </div>
                <div className="space-y-2">
                  <Label>Perekonnanimi *</Label>
                  <Input value={editForm.patient_surname} onChange={e => setEditForm({ ...editForm, patient_surname: e.target.value })} required data-testid="edit-ulcer-surname" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Isikukood (11 numbrit) *</Label>
                <Input value={editForm.patient_id} onChange={e => setEditForm({ ...editForm, patient_id: e.target.value.replace(/\D/g, "").slice(0, 11) })} required maxLength={11} data-testid="edit-ulcer-id" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>Tühista</Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="edit-ulcer-submit">
                  {saving ? "Salvestan..." : "Salvesta"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {questionnaireSession && (
          <TimersQuestionnaire
            session={questionnaireSession}
            caseId={selectedCase.case_id}
            onClose={() => setQuestionnaireSession(null)}
            onSaved={() => { setQuestionnaireSession(null); fetchCase(selectedCase.case_id); }}
          />
        )}

        <BottomNav />
      </div>
    );
  }

  // ==================== CASES LIST VIEW ====================
  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="ulcers-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64 min-h-screen">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Diabeetiline jalahaavand</h1>
                <p className="text-sm text-slate-500">{cases.length} patsienti</p>
              </div>
            </div>
            <Button onClick={() => setNewPatientOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="ulcer-add-patient-btn">
              <Plus className="w-4 h-4 mr-2" /> Lisa patsient
            </Button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
          <UlcerGuidelinesCard open={guidelinesOpen} onToggle={() => setGuidelinesOpen(!guidelinesOpen)} />

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Otsi nime või isikukoodi..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="ulcer-search" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="w-8 h-8 spinner" /></div>
          ) : filteredCases.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? "Patsiente ei leitud" : "Patsiente pole veel lisatud"}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {searchQuery ? "Proovige muuta otsingut" : "Lisage uus patsient alustamiseks"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredCases.map(c => (
                <Card key={c.case_id} className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => fetchCase(c.case_id)} data-testid={`ulcer-case-${c.case_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{c.patient_name} {c.patient_surname}</h3>
                        <p className="text-sm text-slate-500">IK: {c.patient_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">{c.session_count} visiiti</p>
                        {c.last_session && (
                          <Badge className={
                            c.last_session.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                            c.last_session.status === "analyzed" ? "bg-sky-100 text-sky-700" :
                            "bg-amber-100 text-amber-700"
                          }>
                            {c.last_session.status === "completed" ? "Hinnatud" : c.last_session.status === "analyzed" ? "Analüüsitud" : "Ootel"}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      <Dialog open={newPatientOpen} onOpenChange={setNewPatientOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Lisa uus patsient</DialogTitle></DialogHeader>
          <form onSubmit={handleCreatePatient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Eesnimi *</Label>
                <Input value={patientForm.patient_name} onChange={e => setPatientForm({ ...patientForm, patient_name: e.target.value })} required data-testid="ulcer-patient-name" />
              </div>
              <div className="space-y-2">
                <Label>Perekonnanimi *</Label>
                <Input value={patientForm.patient_surname} onChange={e => setPatientForm({ ...patientForm, patient_surname: e.target.value })} required data-testid="ulcer-patient-surname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Isikukood (11 numbrit) *</Label>
              <Input value={patientForm.patient_id} onChange={e => setPatientForm({ ...patientForm, patient_id: e.target.value.replace(/\D/g, "").slice(0, 11) })} required maxLength={11} data-testid="ulcer-patient-id" />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setNewPatientOpen(false)}>Tühista</Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="ulcer-submit-patient">Lisa</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}


// ==================== GUIDELINES ====================
function UlcerGuidelinesCard({ open, onToggle }) {
  return (
    <Card className="border-slate-200 border-l-4 border-l-amber-400" data-testid="ulcer-guidelines-card">
      <CardContent className="p-4">
        <button onClick={onToggle} className="w-full flex items-center justify-between text-left" data-testid="ulcer-guidelines-toggle">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-slate-800">TIMERS haavahindamise juhis</span>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {open && (
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">TIMERS raamistik</h4>
              <p>Diabeetilise jalahaavandi hindamiseks kasutatakse TIMERS raamistikku, mis hõlmab:</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-red-50 rounded-lg p-3">
                <span className="font-bold text-red-700">T – Tissue (Kude)</span>
                <p className="text-xs mt-1">Koetüüpide hindamine: granulatsioon, fibriin, nekroos</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3">
                <span className="font-bold text-yellow-700">I – Infection (Infektsioon)</span>
                <p className="text-xs mt-1">Põletiku tunnused, eritis, tselluliidi oht</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <span className="font-bold text-blue-700">M – Moisture (Niiskus)</span>
                <p className="text-xs mt-1">Haava niiskustase, ümbritseva naha seisund</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <span className="font-bold text-green-700">E – Edge (Servad)</span>
                <p className="text-xs mt-1">Haavaservade seisund, epiteliseerumine</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <span className="font-bold text-purple-700">R – Repair (Taastumine)</span>
                <p className="text-xs mt-1">Paranemise kiirus, lisaravi vajadus</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <span className="font-bold text-orange-700">S – Social (Sotsiaalsed)</span>
                <p className="text-xs mt-1">Patsiendi koostöö, sotsiaalne tugi</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Pildistamise nõuded</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Puhastage haav enne pildistamist (eemaldage vanad sidemed)</li>
                <li>Kasutage hajutatud valgustust, vältige pildistamisel välku</li>
                <li>Pildistage 15-20 cm kauguselt, haav pildi keskmes</li>
                <li>Asetage joonlaud haava kõrvale suuruse hindamiseks</li>
                <li>Kasutage matti, heledavärvilist tausta</li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ==================== UPLOAD ====================
function UlcerUploadCard({ photo, setPhoto, uploading, onUpload, onCancel }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (photo) {
      const url = URL.createObjectURL(photo);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [photo]);

  return (
    <Card className="border-slate-200 border-l-4 border-l-emerald-400" data-testid="ulcer-upload-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Upload className="w-5 h-5 text-emerald-600" /> Laadi haavapilt üles
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors"
        >
          {preview ? (
            <img src={preview} alt="Haavapilt" className="max-h-64 mx-auto rounded-lg" />
          ) : (
            <div className="flex flex-col items-center">
              <Camera className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 font-medium">Klikkige või lohistage haavapilt siia</p>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG või WebP, max 20 MB</p>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
            className="hidden"
            onChange={(e) => { if (e.target.files[0]) setPhoto(e.target.files[0]); }}
            data-testid="ulcer-image-input"
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1">Tühista</Button>
          <Button
            onClick={onUpload}
            disabled={uploading || !photo}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ulcer-upload-btn"
          >
            {uploading ? <span className="w-4 h-4 spinner mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Laadin..." : "Laadi üles ja analüüsi"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== SESSION CARD ====================
function UlcerSessionCard({ session, caseId, index, analyzing, onAnalyze, onDelete, getImageUrl, onImageClick, onQuestionnaire, previousSession, onCompare, comparing, comparisonResult, comparisonData, onDownloadPdf, onPrintPdf, onRefreshSessions, isDoctor }) {
  const [expanded, setExpanded] = useState(index === 1);
  const [editingTimers, setEditingTimers] = useState(false);
  const [editData, setEditData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const date = new Date(session.created_at).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const timersData = session.timers_data;

  const startEditing = () => {
    setEditData(JSON.parse(JSON.stringify(timersData)));
    setEditingTimers(true);
  };

  const saveTimersEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${caseId}/sessions/${session.session_id}/edit-timers`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timers_data: editData }),
      });
      if (res.ok) {
        toast.success("Analüüs parandatud!");
        setEditingTimers(false);
        onRefreshSessions();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Salvestamine ebaõnnestus");
      }
    } catch { toast.error("Salvestamine ebaõnnestus"); }
    finally { setSavingEdit(false); }
  };

  const updateEditField = (sectionKey, fieldKey, value) => {
    setEditData(prev => {
      const copy = { ...prev };
      if (sectionKey === "_root") {
        copy[fieldKey] = value;
      } else {
        copy[sectionKey] = { ...copy[sectionKey], [fieldKey]: value };
      }
      return copy;
    });
  };

  const statusBadge = session.status === "completed"
    ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Hinnatud</Badge>
    : session.status === "analyzed"
      ? <Badge className="bg-sky-100 text-sky-700"><FileText className="w-3 h-3 mr-1" /> Analüüsitud</Badge>
      : session.status === "analyzing" || analyzing
        ? <Badge className="bg-indigo-100 text-indigo-700"><Clock className="w-3 h-3 mr-1 animate-spin" /> Analüüsitakse...</Badge>
        : session.status === "failed"
          ? <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" /> Ebaõnnestunud</Badge>
          : <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Ootel</Badge>;

  const severityBadge = timersData?.severity && {
    kerge: <Badge className="bg-green-100 text-green-700">Kerge</Badge>,
    "mõõdukas": <Badge className="bg-yellow-100 text-yellow-700">Mõõdukas</Badge>,
    raske: <Badge className="bg-orange-100 text-orange-700">Raske</Badge>,
    kriitiline: <Badge className="bg-red-100 text-red-700">Kriitiline</Badge>,
  }[timersData.severity];

  const needsReview = (session.status === "analyzed" || session.status === "completed") && !session.doctor_reviewed;

  return (
    <Card className={needsReview ? "border-red-300 bg-red-50/30" : "border-slate-200"} data-testid={`ulcer-session-${session.session_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="font-semibold text-slate-900">Visiit #{index}</span>
            </button>
            {needsReview && (
              <Badge className="bg-red-100 text-red-700 text-xs" data-testid="nurse-upload-dot">Kontrollimata</Badge>
            )}
            {session.doctor_reviewed && (
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Kontrollitud</Badge>
            )}
            {statusBadge}
            {severityBadge}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{date}</span>
            {(session.status === "pending" || session.status === "failed") && !analyzing && (
              <Button size="sm" variant="outline" onClick={onAnalyze} disabled={analyzing} className="text-sky-600" data-testid="ulcer-analyze-btn">
                <FileText className="w-4 h-4 mr-1" /> Analüüsi
              </Button>
            )}
            {(session.status === "analyzing" || analyzing) && (
              <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
                <span className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                AI analüüsib...
              </div>
            )}
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={onDelete} data-testid="ulcer-delete-session-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="space-y-4 mt-3">
            {/* Image */}
            <div className="flex justify-center">
              <AuthImage
                src={getImageUrl(session.image_path)}
                alt={`Visiit #${index}`}
                className="max-h-80 rounded-lg border border-slate-200"
                onClick={() => onImageClick({ src: getImageUrl(session.image_path), label: `Visiit #${index}` })}
              />
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              {(session.status === "analyzed" || session.status === "completed") && (
                <Button size="sm" variant="outline" onClick={onQuestionnaire} className="text-violet-600 border-violet-200" data-testid="ulcer-questionnaire-btn">
                  <ClipboardList className="w-4 h-4 mr-1" />
                  {session.nurse_questionnaire ? "Vaata küsimustikku" : "Täida küsimustik"}
                </Button>
              )}
              {previousSession && (session.status === "analyzed" || session.status === "completed") && (
                <Button size="sm" variant="outline" onClick={() => onCompare(previousSession.session_id)} disabled={comparing} className="text-indigo-600 border-indigo-200" data-testid="ulcer-compare-btn">
                  {comparing ? <span className="w-4 h-4 spinner mr-1" /> : <ArrowLeftRight className="w-4 h-4 mr-1" />}
                  Võrdle eelmisega
                </Button>
              )}
              {(session.status === "analyzed" || session.status === "completed") && (
                <>
                <Button size="sm" variant="outline" onClick={onPrintPdf} className="text-slate-600 border-slate-200" data-testid="ulcer-print-session-pdf">
                  <Printer className="w-4 h-4 mr-1" /> Prindi
                </Button>
                <Button size="sm" variant="outline" onClick={onDownloadPdf} className="text-emerald-600 border-emerald-200" data-testid="ulcer-download-session-pdf">
                  <Download className="w-4 h-4 mr-1" /> Laadi PDF
                </Button>
                </>
              )}
            </div>

            {/* TIMERS AI Analysis */}
            {session.result_hidden ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid="result-hidden-notice">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="font-semibold text-amber-800">Analüüs teostatud</p>
                <p className="text-sm text-amber-600 mt-1">Ootab arsti kontrolli. Tulemus muutub nähtavaks pärast arsti kinnitust.</p>
              </div>
            ) : (
              <>
                {timersData && (
                  <>
                    {editingTimers && editData ? (
                      <TimersEditor
                        data={editData}
                        onUpdate={updateEditField}
                        onSave={saveTimersEdit}
                        onCancel={() => setEditingTimers(false)}
                        saving={savingEdit}
                      />
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div />
                          {isDoctor && (
                            <Button size="sm" variant="outline" onClick={startEditing} className="text-amber-600 border-amber-200" data-testid="edit-timers-btn">
                              <Edit2 className="w-3.5 h-3.5 mr-1" /> Paranda analüüs
                            </Button>
                          )}
                        </div>
                        <TimersDisplay data={timersData} />
                        {session.timers_edited_by && (
                          <div className="text-xs text-slate-500 mt-1">
                            Parandatud: {session.timers_edited_by}, {session.timers_edited_at}
                          </div>
                        )}
                      </>
                    )}
                    <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700" data-testid="ai-disclaimer-timers">
                      <strong>NB:</strong> See on tehisintellekti (Gemini 3.1 Pro) esialgne hinnang. Lõpliku otsuse teeb arst. Andmeid säilitatakse 10 aastat vastavalt Eesti tervishoiuteenuste korraldamise seadusele (§ 769).
                    </div>
                  </>
                )}

                {/* Raw analysis fallback */}
                {!timersData && session.analysis_result && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-sky-600" /> AI analüüsi tulemus
                    </h4>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{session.analysis_result}</div>
                    <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700" data-testid="ai-disclaimer">
                      <strong>NB:</strong> See on tehisintellekti (Gemini 3.1 Pro) esialgne hinnang. Lõpliku otsuse teeb arst. Andmeid säilitatakse 10 aastat vastavalt Eesti tervishoiuteenuste korraldamise seadusele (§ 769).
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Nurse questionnaire summary */}
            {session.nurse_questionnaire && (
              <div className="bg-violet-50 rounded-lg p-4">
                <h4 className="font-semibold text-violet-800 mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" /> Õe hinnang (TIMERS)
                </h4>
                <QuestionnaireReadonly data={session.nurse_questionnaire} />
              </div>
            )}

            {/* Comparison result */}
            {(comparisonResult || comparisonData) && (
              <div className="bg-indigo-50 rounded-lg p-4" data-testid="comparison-result">
                <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4" /> Eelmise visiidiga võrdlus
                </h4>
                {comparisonData ? (
                  <ComparisonDisplay data={comparisonData} />
                ) : (
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{comparisonResult}</div>
                )}
              </div>
            )}

            {/* Doctor Review */}
            {(session.status === "analyzed" || session.status === "completed") && !session.result_hidden && (
              <DoctorReview
                reviewUrl={`/ulcers/cases/${caseId}/sessions/${session.session_id}/doctor-review`}
                sendUrl={`/ulcers/cases/${caseId}/sessions/${session.session_id}/send-review-to-nurse`}
                record={session}
                onUpdate={onRefreshSessions}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ==================== TIMERS DISPLAY ====================
function TimersDisplay({ data }) {
  const sections = [
    { key: "tissue", label: "T – Kude", color: "red" },
    { key: "infection", label: "I – Infektsioon", color: "yellow" },
    { key: "moisture", label: "M – Niiskus", color: "blue" },
    { key: "edge", label: "E – Servad", color: "green" },
    { key: "repair", label: "R – Taastumine", color: "purple" },
    { key: "social", label: "S – Sotsiaalsed", color: "orange" },
  ];

  const scoreColors = {
    hea: "text-green-700 bg-green-100",
    "mõõdukas": "text-yellow-700 bg-yellow-100",
    halb: "text-red-700 bg-red-100",
    madal: "text-green-700 bg-green-100",
    "kõrge": "text-red-700 bg-red-100",
    kuiv: "text-orange-700 bg-orange-100",
    optimaalne: "text-green-700 bg-green-100",
    liigniiske: "text-blue-700 bg-blue-100",
    tugev_eksudaat: "text-red-700 bg-red-100",
  };

  const dims = data.wound_dimensions;

  return (
    <div className="space-y-3" data-testid="timers-display">
      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
        <FileText className="w-4 h-4 text-sky-600" /> EWMA / TIME analüüs
      </h4>

      {/* Wound Dimensions */}
      {dims && (
        <div className="bg-slate-900 text-white rounded-lg p-4" data-testid="wound-dimensions">
          <div className="flex items-center justify-between">
            <h5 className="font-semibold text-sm flex items-center gap-2">
              <Activity className="w-4 h-4" /> Haava mõõtmed
            </h5>
            <Badge className="bg-white/20 text-white text-xs">{dims.measurement_method}</Badge>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold">{dims.length_cm}</p>
              <p className="text-xs text-slate-300">Pikkus (cm)</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{dims.width_cm}</p>
              <p className="text-xs text-slate-300">Laius (cm)</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{dims.area_cm2}</p>
              <p className="text-xs text-slate-300">Pindala (cm²)</p>
            </div>
          </div>
        </div>
      )}

      {/* TIMERS Sections */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sections.map(({ key, label, color }) => {
          const section = data[key];
          if (!section) return null;
          const score = section.score;
          return (
            <div key={key} className={`bg-${color}-50 rounded-lg p-3 border border-${color}-100`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`font-bold text-${color}-700 text-sm`}>{label}</span>
                {score && <Badge className={scoreColors[score] || "bg-slate-100 text-slate-700"}>{score}</Badge>}
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">{section.description}</p>

              {/* Tissue specifics */}
              {key === "tissue" && section.granulation_pct != null && (
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded">Granulatsioon: {section.granulation_pct}%</span>
                  {(section.slough_pct > 0) && <span className="bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">Koorik: {section.slough_pct}%</span>}
                  {(section.nekroos_pct > 0 || section.eschar_pct > 0) && <span className="bg-red-200 text-red-800 px-2 py-0.5 rounded">Nekroos: {section.nekroos_pct || section.eschar_pct}%</span>}
                  {section.epithelial_pct > 0 && <span className="bg-pink-200 text-pink-800 px-2 py-0.5 rounded">Epiteel: {section.epithelial_pct}%</span>}
                </div>
              )}
              {key === "tissue" && (section.nekroos_warning || section.debridement_warning) && (
                <div className="mt-2 bg-red-100 border border-red-300 rounded p-2 text-xs text-red-800 font-medium">
                  <AlertCircle className="w-3 h-3 inline mr-1" />{section.nekroos_warning || section.debridement_warning}
                </div>
              )}

              {/* Infection specifics */}
              {key === "infection" && section.erythema_present && (
                <div className="mt-1 text-xs text-red-600 font-medium">Erüteem: {section.erythema_extent_cm}</div>
              )}
              {key === "infection" && section.inflammation_vs_infection && (
                <div className="mt-1 text-xs text-slate-500">Hinnang: {section.inflammation_vs_infection}</div>
              )}
              {key === "infection" && section.nurse_validation_needed && (
                <div className="mt-2 bg-yellow-100 border border-yellow-300 rounded p-2 text-xs text-yellow-800">
                  <ClipboardList className="w-3 h-3 inline mr-1" />Õde peab kontrollima: {section.nurse_validation_needed}
                </div>
              )}

              {/* Moisture specifics */}
              {key === "moisture" && section.exudate_level && (
                <div className="mt-1 text-xs text-slate-500">Eksudaat: {section.exudate_level}</div>
              )}
              {key === "moisture" && section.periwound_protection && (
                <div className="mt-1 text-xs text-blue-700 font-medium">{section.periwound_protection}</div>
              )}

              {/* Edge specifics */}
              {key === "edge" && section.contraction_assessment && (
                <div className="mt-1 text-xs text-slate-500">Kinnikasvamine: {section.contraction_assessment}</div>
              )}
              {key === "edge" && section.stalled && (
                <div className="mt-2 bg-orange-100 border border-orange-300 rounded p-2 text-xs text-orange-800 font-medium">
                  <AlertCircle className="w-3 h-3 inline mr-1" />Paranemine seiskunud!
                  {section.stalled_action && <span> → {section.stalled_action}</span>}
                </div>
              )}

              {/* Repair specifics */}
              {key === "repair" && section.escalation_needed && (
                <div className="mt-2 bg-purple-100 border border-purple-300 rounded p-2 text-xs text-purple-800 font-medium">
                  <AlertCircle className="w-3 h-3 inline mr-1" />Edasisuunamine vajalik: {section.escalation_options}
                </div>
              )}

              {/* Dressing recommendation per section */}
              {section.recommended_dressing && (
                <div className="mt-2 text-xs bg-white/80 rounded p-1.5 text-slate-700">
                  <span className="font-medium">Side:</span> {section.recommended_dressing}
                </div>
              )}
              {section.recommended_action && (
                <div className="mt-1 text-xs bg-white/80 rounded p-1.5 text-slate-700">
                  <span className="font-medium">Tegevus:</span> {section.recommended_action}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dressing Recommendation */}
      {data.dressing_recommendation && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4" data-testid="dressing-recommendation">
          <h5 className="font-semibold text-teal-800 text-sm mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Sidesoovitus
          </h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="text-teal-600 font-medium">Peamine:</span> {data.dressing_recommendation.primary_dressing}</div>
            {data.dressing_recommendation.secondary_dressing && (
              <div><span className="text-teal-600 font-medium">Sekundaarne:</span> {data.dressing_recommendation.secondary_dressing}</div>
            )}
            <div><span className="text-teal-600 font-medium">Vahetussagedus:</span> {data.dressing_recommendation.change_frequency}</div>
          </div>
          {data.dressing_recommendation.rationale && (
            <p className="mt-2 text-xs text-teal-700">{data.dressing_recommendation.rationale}</p>
          )}
        </div>
      )}

      {/* Nurse Action Prompts */}
      {data.nurse_action_prompts && data.nurse_action_prompts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="nurse-action-prompts">
          <h5 className="font-semibold text-amber-800 text-sm mb-2 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Õe tegevusjuhised (aruta arstiga)
          </h5>
          <ul className="space-y-1.5">
            {data.nurse_action_prompts.map((prompt, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                <span className="font-bold text-amber-600 mt-0.5 text-xs">{i + 1}.</span>
                {prompt}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Overall Summary */}
      {data.overall_summary && (
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
          <h5 className="font-semibold text-slate-800 text-sm mb-1">Kokkuvõte</h5>
          <p className="text-sm text-slate-700">{data.overall_summary}</p>
        </div>
      )}
    </div>
  );
}


// ==================== TIMERS EDITOR ====================
function TimersEditor({ data, onUpdate, onSave, onCancel, saving }) {
  const sections = [
    { key: "tissue", label: "T – Kude", fields: [
      { k: "score", label: "Hinnang", type: "select", options: ["hea", "mõõdukas", "halb"] },
      { k: "granulation_pct", label: "Granulatsioon %", type: "number" },
      { k: "slough_pct", label: "Koorik %", type: "number" },
      { k: "nekroos_pct", label: "Nekroos %", type: "number" },
      { k: "epithelial_pct", label: "Epiteel %", type: "number" },
      { k: "description", label: "Kirjeldus", type: "textarea" },
      { k: "recommended_action", label: "Soovitus", type: "textarea" },
    ]},
    { key: "infection", label: "I – Infektsioon", fields: [
      { k: "score", label: "Hinnang", type: "select", options: ["madal", "mõõdukas", "kõrge"] },
      { k: "description", label: "Kirjeldus", type: "textarea" },
      { k: "recommended_dressing", label: "Side", type: "text" },
    ]},
    { key: "moisture", label: "M – Niiskus", fields: [
      { k: "score", label: "Hinnang", type: "select", options: ["kuiv", "optimaalne", "liigniiske", "tugev_eksudaat"] },
      { k: "exudate_level", label: "Eksudaat", type: "text" },
      { k: "description", label: "Kirjeldus", type: "textarea" },
      { k: "recommended_dressing", label: "Side", type: "text" },
    ]},
    { key: "edge", label: "E – Servad", fields: [
      { k: "score", label: "Hinnang", type: "select", options: ["hea", "mõõdukas", "halb"] },
      { k: "contraction_assessment", label: "Kinnikasvamine", type: "select", options: ["aktiivne", "aeglane", "seiskunud"] },
      { k: "description", label: "Kirjeldus", type: "textarea" },
    ]},
    { key: "repair", label: "R – Taastumine", fields: [
      { k: "score", label: "Hinnang", type: "select", options: ["hea", "mõõdukas", "halb"] },
      { k: "healing_trajectory", label: "Paranemise suund", type: "select", options: ["paraneb", "seiskunud", "halveneb"] },
      { k: "description", label: "Kirjeldus", type: "textarea" },
    ]},
  ];

  return (
    <div className="space-y-4 border-2 border-amber-300 rounded-lg p-4 bg-amber-50/50" data-testid="timers-editor">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-amber-800 flex items-center gap-2">
          <Edit2 className="w-4 h-4" /> Analüüsi parandamine
        </h4>
        <div className="flex gap-2">
          <Button size="sm" onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-timers-edit">
            {saving ? "Salvestan..." : "Salvesta"}
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} data-testid="cancel-timers-edit">Tühista</Button>
        </div>
      </div>

      {/* Wound Dimensions */}
      {data.wound_dimensions && (
        <div className="bg-white rounded-lg p-3 border border-slate-200">
          <h5 className="font-semibold text-sm text-slate-700 mb-2">Haava mõõtmed</h5>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Pikkus (cm)</Label>
              <Input type="number" step="0.1" value={data.wound_dimensions.length_cm || ""} onChange={e => onUpdate("wound_dimensions", "length_cm", parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Laius (cm)</Label>
              <Input type="number" step="0.1" value={data.wound_dimensions.width_cm || ""} onChange={e => onUpdate("wound_dimensions", "width_cm", parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Pindala (cm²)</Label>
              <Input type="number" step="0.1" value={data.wound_dimensions.area_cm2 || ""} onChange={e => onUpdate("wound_dimensions", "area_cm2", parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
            </div>
          </div>
        </div>
      )}

      {/* TIMERS Sections */}
      {sections.map(({ key, label, fields }) => {
        const section = data[key];
        if (!section) return null;
        return (
          <div key={key} className="bg-white rounded-lg p-3 border border-slate-200">
            <h5 className="font-semibold text-sm text-slate-700 mb-2">{label}</h5>
            <div className="space-y-2">
              {fields.map(f => (
                <div key={f.k}>
                  <Label className="text-xs text-slate-500">{f.label}</Label>
                  {f.type === "select" ? (
                    <select
                      className="w-full h-8 rounded border border-slate-200 text-sm px-2"
                      value={section[f.k] || ""}
                      onChange={e => onUpdate(key, f.k, e.target.value)}
                    >
                      <option value="">-</option>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea
                      className="w-full rounded border border-slate-200 text-sm p-2 min-h-[60px]"
                      value={section[f.k] || ""}
                      onChange={e => onUpdate(key, f.k, e.target.value)}
                    />
                  ) : f.type === "number" ? (
                    <Input type="number" step="1" min="0" max="100" className="h-8 text-sm"
                      value={section[f.k] ?? ""}
                      onChange={e => onUpdate(key, f.k, parseFloat(e.target.value) || 0)}
                    />
                  ) : (
                    <Input className="h-8 text-sm" value={section[f.k] || ""}
                      onChange={e => onUpdate(key, f.k, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Overall Summary */}
      <div className="bg-white rounded-lg p-3 border border-slate-200">
        <Label className="text-xs text-slate-500">Kokkuvõte</Label>
        <textarea
          className="w-full rounded border border-slate-200 text-sm p-2 min-h-[60px]"
          value={data.overall_summary || ""}
          onChange={e => onUpdate("_root", "overall_summary", e.target.value)}
        />
      </div>
      <div className="bg-white rounded-lg p-3 border border-slate-200">
        <Label className="text-xs text-slate-500">Raskusaste</Label>
        <select
          className="w-full h-8 rounded border border-slate-200 text-sm px-2"
          value={data.severity || ""}
          onChange={e => onUpdate("_root", "severity", e.target.value)}
        >
          <option value="kerge">Kerge</option>
          <option value="mõõdukas">Mõõdukas</option>
          <option value="raske">Raske</option>
          <option value="kriitiline">Kriitiline</option>
        </select>
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-timers-edit-bottom">
          {saving ? "Salvestan..." : "Salvesta parandused"}
        </Button>
        <Button variant="outline" onClick={onCancel}>Tühista</Button>
      </div>
    </div>
  );
}


// ==================== COMPARISON DISPLAY ====================
function ComparisonDisplay({ data }) {
  const dim = data.dimension_change;
  const trendColors = { paranemine: "text-green-700", halvenemine: "text-red-700", muutuseta: "text-slate-500" };
  const overallColors = { paraneb: "bg-green-100 text-green-800", seiskunud: "bg-yellow-100 text-yellow-800", halveneb: "bg-red-100 text-red-800" };

  const changes = [
    { label: "Kude (T)", data: data.tissue_change },
    { label: "Infektsioon (I)", data: data.infection_change },
    { label: "Niiskus (M)", data: data.moisture_change },
    { label: "Servad (E)", data: data.edge_change },
  ].filter(c => c.data);

  return (
    <div className="space-y-3">
      {/* Dimension change */}
      {dim && (
        <div className="bg-white rounded-lg p-3 border border-indigo-200" data-testid="dimension-comparison">
          <h5 className="font-semibold text-indigo-800 text-sm mb-2">Mõõtmete muutus</h5>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs text-slate-500">Eelmine</p>
              <p className="font-bold text-slate-700">{dim.previous_area_cm2} cm²</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Praegune</p>
              <p className="font-bold text-slate-700">{dim.current_area_cm2} cm²</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Muutus</p>
              <p className={`font-bold ${dim.change_pct < 0 ? "text-green-600" : dim.change_pct > 0 ? "text-red-600" : "text-slate-600"}`}>
                {dim.change_pct > 0 ? "+" : ""}{dim.change_pct}%
              </p>
            </div>
          </div>
          <div className="mt-2 text-center">
            <Badge className={dim.trend === "kahaneb" ? "bg-green-100 text-green-700" : dim.trend === "kasvab" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}>
              {dim.trend === "kahaneb" ? "Haav kahaneb" : dim.trend === "kasvab" ? "Haav kasvab" : "Muutuseta"}
            </Badge>
          </div>
        </div>
      )}

      {/* Parameter changes */}
      <div className="space-y-2">
        {changes.map(({ label, data: ch }) => (
          <div key={label} className="flex items-start justify-between bg-white rounded-lg p-2.5 border border-indigo-100">
            <div className="flex-1">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              {ch.description && <p className="text-xs text-slate-500 mt-0.5">{ch.description}</p>}
            </div>
            <span className={`text-sm font-semibold ${trendColors[ch.trend] || "text-slate-500"}`}>
              {ch.trend === "paranemine" ? "Paraneb" : ch.trend === "halvenemine" ? "Halveneb" : "Muutuseta"}
            </span>
          </div>
        ))}
      </div>

      {/* Overall trend */}
      {data.overall_trend && (
        <div className="text-center">
          <Badge className={`text-sm px-4 py-1 ${overallColors[data.overall_trend] || "bg-slate-100 text-slate-700"}`}>
            Üldine trend: {data.overall_trend === "paraneb" ? "Paraneb" : data.overall_trend === "halveneb" ? "Halveneb" : "Seiskunud"}
          </Badge>
        </div>
      )}

      {/* Escalation */}
      {data.escalation_needed && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          <span className="font-medium">Eskalatsioon vajalik!</span>
        </div>
      )}

      {/* Action recommendations */}
      {data.action_recommendations && data.action_recommendations.length > 0 && (
        <div className="space-y-1">
          <h5 className="font-semibold text-indigo-800 text-xs">Soovitused:</h5>
          {data.action_recommendations.map((rec, i) => (
            <p key={i} className="text-sm text-slate-700">• {rec}</p>
          ))}
        </div>
      )}

      {/* Summary */}
      {data.summary && (
        <p className="text-sm text-slate-700 italic">{data.summary}</p>
      )}
    </div>
  );
}


// ==================== RADIO QUESTION ====================
function RadioQ({ label, value, onChange, alert: alertText }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-slate-700">{label}</p>
      {alertText && <p className="text-xs text-red-600 font-medium">{alertText}</p>}
      <div className="flex gap-3">
        <button type="button" onClick={() => onChange(true)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${value === true ? "bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Jah</button>
        <button type="button" onClick={() => onChange(false)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${value === false ? "bg-red-100 text-red-700 ring-2 ring-red-300" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Ei</button>
      </div>
    </div>
  );
}


// ==================== TIMERS QUESTIONNAIRE ====================
function TimersQuestionnaire({ session, caseId, onClose, onSaved }) {
  const existing = session.nurse_questionnaire || {};
  const [answers, setAnswers] = useState({
    tissue_agrees: existing.tissue_agrees ?? null,
    tissue_correction: existing.tissue_correction || "",
    tissue_blood_supply_ok: existing.tissue_blood_supply_ok ?? null,
    infection_signs: existing.infection_signs || { smell: false, purulent: false, pain_increase: false, glucose_rise: false },
    infection_erythema_over_2cm: existing.infection_erythema_over_2cm ?? null,
    moisture_skin: existing.moisture_skin || "",
    edge_cause: existing.edge_cause || "",
    repair_stalled_over_4w: existing.repair_stalled_over_4w ?? null,
    social_offloading: existing.social_offloading ?? null,
    social_support: existing.social_support ?? null,
    notes: existing.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/ulcers/cases/${caseId}/sessions/${session.session_id}/questionnaire`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ questionnaire: answers }),
      });
      if (res.ok) {
        toast.success("Küsimustik salvestatud");
        onSaved();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Salvestamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-violet-600" />
            TIMERS küsimustik – Visiit #{session.session_id?.slice(-6)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* T - Tissue */}
          <div className="bg-red-50 rounded-lg p-4 space-y-3">
            <h4 className="font-bold text-red-700">T – Tissue (Kude)</h4>
            <RadioQ
              label="Kas AI tuvastatud koetüübid ühtivad teie visuaalse hinnanguga pärast haava puhastamist?"
              value={answers.tissue_agrees}
              onChange={v => setAnswers({ ...answers, tissue_agrees: v })}
            />
            {answers.tissue_agrees === false && (
              <div className="space-y-1">
                <Label className="text-xs">Korrigeering (nt sidemejäägi varjamine):</Label>
                <Input value={answers.tissue_correction} onChange={e => setAnswers({ ...answers, tissue_correction: e.target.value })} placeholder="Kirjeldage erinevust..." data-testid="tissue-correction-input" />
              </div>
            )}
            <div className="border-t border-red-200 pt-3">
              <p className="text-xs text-red-700 font-bold mb-1">KRIITILINE OHUTUSE KÜSIMUS:</p>
              <RadioQ
                label="Kui haavas on musta nekroosi ja AI soovitab paksenenud koe eemaldamist – kas patsiendi jala verevarustus on piisav (ABI > 0.5)?"
                value={answers.tissue_blood_supply_ok}
                onChange={v => setAnswers({ ...answers, tissue_blood_supply_ok: v })}
                alert={answers.tissue_blood_supply_ok === false ? "STOP! Jalg on isheemiline – ei tohi lõigata! Hoida haav kuivana ja konsulteeri veresoontekirurgiga." : null}
              />
            </div>
          </div>

          {/* I - Infection */}
          <div className="bg-yellow-50 rounded-lg p-4 space-y-3">
            <h4 className="font-bold text-yellow-700">I – Infection (Infektsioon)</h4>
            <p className="text-sm text-slate-700">Kas patsiendil esineb kliinilisi infektsiooni tunnuseid, mida fotolt ei tuvasta?</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "smell", label: "Spetsiifiline haava lõhn" },
                { key: "purulent", label: "Mädane eritis" },
                { key: "pain_increase", label: "Valu äkiline suurenemine" },
                { key: "glucose_rise", label: "Seletamatu glükoosi tõus (24h)" },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={answers.infection_signs[key]}
                    onChange={e => setAnswers({ ...answers, infection_signs: { ...answers.infection_signs, [key]: e.target.checked } })}
                    className="rounded border-slate-300"
                  />
                  {label}
                </label>
              ))}
            </div>
            <RadioQ
              label="Kas erüteem ulatub haavaservast kaugemale kui 2 cm (tselluliidi oht)?"
              value={answers.infection_erythema_over_2cm}
              onChange={v => setAnswers({ ...answers, infection_erythema_over_2cm: v })}
              alert={answers.infection_erythema_over_2cm === true ? "Vajalik arsti konsultatsioon / antibiootikumravi!" : null}
            />
          </div>

          {/* M - Moisture */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <h4 className="font-bold text-blue-700">M – Moisture (Niiskus)</h4>
            <p className="text-sm text-slate-700">Kuidas mõjutab haavaeritis ümbritsevat nahka?</p>
            <div className="flex flex-col gap-2">
              {[
                { val: "terve", label: "Ümbritsev nahk on terve" },
                { val: "matsereeritud", label: "Esineb matseratsioon (valge, pehme nahk) – AI soovitatud sidemele lisada naha kaitsebarjäär" },
                { val: "kuiv", label: "Ümbritseval nahal on ekseem või nahk on liiga kuiv" },
              ].map(({ val, label }) => (
                <button
                  key={val}
                  onClick={() => setAnswers({ ...answers, moisture_skin: val })}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${answers.moisture_skin === val ? "bg-blue-200 text-blue-800 ring-2 ring-blue-300" : "bg-white text-slate-600 hover:bg-blue-100"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* E - Edge */}
          <div className="bg-green-50 rounded-lg p-4 space-y-3">
            <h4 className="font-bold text-green-700">E – Edge (Servad)</h4>
            <p className="text-sm text-slate-700">Kui AI hoiatab, et paranemine on seiskunud – mis on põhjus?</p>
            <div className="flex flex-col gap-2">
              {[
                { val: "hüperkeratoos", label: "Haava servas on paks sarvkiht/kallus (hüperkeratoos)" },
                { val: "undermining", label: "Haavaserva all on taskud/õõnsused" },
                { val: "epibole", label: "Servad on rullunud (epibole)" },
                { val: "none", label: "Ei ole asjakohane / paranemine toimub" },
              ].map(({ val, label }) => (
                <button
                  key={val}
                  onClick={() => setAnswers({ ...answers, edge_cause: val })}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${answers.edge_cause === val ? "bg-green-200 text-green-800 ring-2 ring-green-300" : "bg-white text-slate-600 hover:bg-green-100"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* R - Repair */}
          <div className="bg-purple-50 rounded-lg p-4 space-y-3">
            <h4 className="font-bold text-purple-700">R – Repair (Taastumine)</h4>
            <RadioQ
              label="Kas haava paranemine seiskunud rohkem kui 4 nädalat vaatamata õigele standardravile?"
              value={answers.repair_stalled_over_4w}
              onChange={v => setAnswers({ ...answers, repair_stalled_over_4w: v })}
              alert={answers.repair_stalled_over_4w === true ? "Aeg delegeerida juhtum edasi – kaaluda kollageenmaatriksit, vaakumravi (NPWT), hapnikravi." : null}
            />
          </div>

          {/* S - Social */}
          <div className="bg-orange-50 rounded-lg p-4 space-y-3">
            <h4 className="font-bold text-orange-700">S – Social (Sotsiaalsed tegurid)</h4>
            <RadioQ
              label="Kas patsient täidab koormuse vähendamise (offloading) režiimi?"
              value={answers.social_offloading}
              onChange={v => setAnswers({ ...answers, social_offloading: v })}
              alert={answers.social_offloading === false ? "Haav ei parane enne, kui offloading-probleem on lahendatud!" : null}
            />
            <RadioQ
              label="Kas patsiendil on kodus piisavalt sotsiaalset tuge ja vahendeid?"
              value={answers.social_support}
              onChange={v => setAnswers({ ...answers, social_support: v })}
              alert={answers.social_support === false ? "Vajalik sotsiaaltöötaja või koduõenduse kaasamine." : null}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="font-medium">Lisamärkused</Label>
            <textarea
              value={answers.notes}
              onChange={e => setAnswers({ ...answers, notes: e.target.value })}
              className="w-full rounded-lg border border-slate-300 p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
              placeholder="Täiendavad märkused..."
              data-testid="questionnaire-notes"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>Tühista</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-questionnaire-btn">
              {saving ? "Salvestan..." : "Salvesta küsimustik"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ==================== QUESTIONNAIRE READONLY ====================
function QuestionnaireReadonly({ data }) {
  const items = [
    { label: "Kude – AI ühtib visuaalsega", value: data.tissue_agrees, type: "bool" },
    data.tissue_correction && { label: "Kude – Korrigeering", value: data.tissue_correction },
    { label: "Kude – Verevarustus piisav eemaldamiseks", value: data.tissue_blood_supply_ok, type: "bool", critical: true },
    { label: "Infektsioon – Erüteem >2cm", value: data.infection_erythema_over_2cm, type: "bool", critical: true },
    { label: "Niiskus – Ümbritsev nahk", value: data.moisture_skin },
    { label: "Servad – Seiskunud põhjus", value: data.edge_cause },
    { label: "Taastumine – Seiskunud >4 nädalat", value: data.repair_stalled_over_4w, type: "bool" },
    { label: "Offloading režiim", value: data.social_offloading, type: "bool" },
    { label: "Sotsiaalne tugi", value: data.social_support, type: "bool" },
    data.notes && { label: "Märkused", value: data.notes },
  ].filter(Boolean);

  const infectionSigns = data.infection_signs || {};
  const activeInfections = Object.entries(infectionSigns)
    .filter(([, v]) => v)
    .map(([k]) => ({ smell: "Lõhn", purulent: "Mädane eritis", pain_increase: "Valu tõus", glucose_rise: "Glükoosi tõus" }[k] || k));

  return (
    <div className="space-y-2 text-sm">
      {items.map((item, i) => (
        <div key={i} className={`flex justify-between items-center ${item.critical && item.value === false ? "text-red-700 font-medium" : ""}`}>
          <span className="text-slate-600">{item.label}:</span>
          <span className="font-medium">
            {item.type === "bool" ? (item.value === true ? "Jah" : item.value === false ? "Ei" : "–") : (item.value || "–")}
          </span>
        </div>
      ))}
      {activeInfections.length > 0 && (
        <div className="flex justify-between items-center">
          <span className="text-slate-600">Infektsiooni tunnused:</span>
          <span className="font-medium text-red-600">{activeInfections.join(", ")}</span>
        </div>
      )}
    </div>
  );
}


// ==================== AUTH IMAGE ====================
function AuthImage({ src, alt, className, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("session_token");
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.ok ? res.blob() : null)
      .then(blob => {
        if (blob && !cancelled) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [src]);

  if (!blobUrl) return <div className={`${className} bg-slate-100 flex items-center justify-center`}><Camera className="w-6 h-6 text-slate-300" /></div>;
  return <img src={blobUrl} alt={alt} className={`${className} ${onClick ? "cursor-zoom-in" : ""}`} onClick={onClick} />;
}


// ==================== HOME CARE GUIDE BUTTON ====================
function HomeCareGuideButton({ caseId, surname }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const downloadGuide = async (lang) => {
    setOpen(false);
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/ulcers/cases/${caseId}/home-care-guide?lang=${lang}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { toast.error("Allalaadimine ebaõnnestus"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const names = { et: "Kodune_juhend", ru: "Instruktsiya", en: "Home_care_guide" };
      a.download = `${names[lang] || "Guide"}_${surname || "patsient"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Allalaadimine ebaõnnestus"); }
  };

  const langs = [
    { code: "et", label: "Eesti keeles", flag: "🇪🇪" },
    { code: "ru", label: "На русском", flag: "🇷🇺" },
    { code: "en", label: "In English", flag: "🇬🇧" },
  ];

  return (
    <div className="relative" ref={ref}>
      <Button size="sm" variant="outline" onClick={() => setOpen(!open)} className="text-teal-600 border-teal-200" data-testid="ulcer-home-care-guide-btn">
        <Home className="w-4 h-4 mr-1" /> Kodune juhend <ChevronDown className="w-3 h-3 ml-1" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[180px] py-1">
          {langs.map(({ code, label, flag }) => (
            <button
              key={code}
              onClick={() => downloadGuide(code)}
              className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              data-testid={`home-care-guide-${code}`}
            >
              <span>{flag}</span> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
